import { useEffect, useState } from "react";
import api, { errorMessage } from "../../lib/api";
import { Modal } from "../Modal";
import { ActionButton, GhostButton, inputClass } from "../Primitives";
import { toast } from "sonner";
import { Send, AlertCircle } from "lucide-react";

/**
 * CoachGuardModal — one-click nudge from the guard-edit drawer.
 *
 * Pulls a pre-composed subject/body from `/api/guards/:id/coach/preview`
 * (guard name + top-2 offense counts over the past 30 days), lets the
 * dispatcher tweak either field, then POSTs to `/coach/send` which mails
 * the guard via Resend + audit-logs to `db.coaching_logs`.
 */
export function CoachGuardModal({ open, onClose, guard }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    if (!open || !guard?.id) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(`/guards/${guard.id}/coach/preview`);
        if (cancelled) return;
        setPreview(r.data);
        setSubject(r.data?.subject || "");
        setBody(r.data?.body || "");
      } catch (e) {
        if (!cancelled) toast.error(errorMessage(e, "Failed to compose message"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, guard?.id]);

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/guards/${guard.id}/coach/send`, { subject, body });
      if (r.data?.sent) {
        toast.success(`Coaching message sent to ${preview?.guard_email}`);
      } else {
        toast.info(`Message recorded — email disabled (${r.data?.reason || "no provider"})`);
      }
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to send"));
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  const summary = preview?.summary;
  const canSend = preview?.can_send;

  return (
    <Modal onClose={onClose} title={`Coach ${guard?.name || "Officer"}`} testId="coach-guard-modal">
      <div className="p-5 space-y-4">
        {loading && (
          <div className="text-center py-6 text-zinc-500 scanlines">Composing message…</div>
        )}

        {!loading && preview && (
          <>
            {/* Summary chips */}
            <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
              <div className="label-mute mb-2">Past {summary?.days ?? 30} days</div>
              <div className="flex flex-wrap gap-2">
                <Chip color="#0A84FF" label="Check-ins" value={summary?.counts?.check_in ?? 0} />
                <Chip color="#FF9F0A" label="Late (>15m)" value={summary?.counts?.late ?? 0} />
                <Chip color="#FF9F0A" label="Missed C/O" value={summary?.counts?.missed_clockout ?? 0} />
                <Chip color="#FF3B30" label="Fence breaches" value={summary?.counts?.breach ?? 0} />
              </div>
            </div>

            {!canSend && (
              <div
                className="flex items-start gap-2 border border-[#FF9F0A4D] bg-[#FF9F0A0F] px-3 py-2 text-xs text-[#FF9F0A]"
                data-testid="coach-guard-no-email"
              >
                <AlertCircle className="size-3.5 shrink-0 mt-0.5" />
                <span>
                  This  has no email on file. Add one on the profile before sending.
                </span>
              </div>
            )}

            {/* Editable subject + body */}
            <div>
              <div className="label-mute mb-1">To</div>
              <div className="text-xs text-zinc-300 font-mono">
                {preview.guard_email || <span className="text-zinc-500">not on file</span>}
              </div>
            </div>

            <div>
              <div className="label-mute mb-1">Subject</div>
              <input
                data-testid="coach-guard-subject"
                type="text"
                className={inputClass}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
              />
            </div>

            <div>
              <div className="label-mute mb-1">Message</div>
              <textarea
                data-testid="coach-guard-body"
                rows={9}
                className={inputClass}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={4000}
              />
              <div className="text-[10px] text-zinc-500 mt-1 text-right font-mono tabular-nums">
                {body.length} / 4000
              </div>
            </div>
          </>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#27272A]">
        <GhostButton onClick={onClose} data-testid="coach-guard-cancel">Cancel</GhostButton>
        <ActionButton
          onClick={send}
          disabled={sending || loading || !canSend}
          data-testid="coach-guard-send"
        >
          <Send className="size-3.5" />
          {sending ? "Sending…" : "Send message"}
        </ActionButton>
      </div>
    </Modal>
  );
}

function Chip({ color, label, value }) {
  return (
    <div
      className="inline-flex items-center gap-1.5 border px-2 py-1 text-[10px] font-mono uppercase tracking-widest"
      style={{ borderColor: `${color}4D`, color }}
    >
      <span>{label}</span>
      <span className="text-white font-bold tabular-nums">{value}</span>
    </div>
  );
}
